import { Clickable, Col, Dropdown, Icon, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import { Api, QUIZ_ROLE, QUIZ_TYPE, getQuizRole, theme } from 'lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Header, IrreversableModal, ParticipationSummary } from 'screens/Admin/components';
import Pagination from '../Moderation/components/Pagination';
import BulkCreateForm from './components/BulkCreateForm';
import QuizForm from './components/QuizForm';
import QuizListTable from './components/QuizListTable';
import QuizParticipantsModal from './components/QuizParticipantsModal';
import TeamPicker from './components/TeamPicker';

const TYPE_FILTER_OPTIONS = [
  { value: 'All', opt: undefined },
  { value: 'Daily', opt: QUIZ_TYPE.DAILY },
  { value: 'Half-time', opt: QUIZ_TYPE.HALF_TIME }
];

const ACTIVE_FILTER_OPTIONS = [
  { value: 'All', opt: undefined },
  { value: 'Active', opt: 'true' },
  { value: 'Inactive', opt: 'false' }
];

const PAGE = 20;

// Sentinel for the ?team param meaning "no channel filter" (P4P admins only).
const ALL_CHANNELS = 'all';

const fmtNumber = (n) => (n || 0).toLocaleString();
const fmtAccuracy = (a) => `${Math.round((a || 0) * 1000) / 10}%`;

// The open form's field values ride along in the URL as a JSON `draft` param so a shared
// link reopens the in-progress create/edit exactly as it was left.
const encodeDraft = (obj) => {
  try {
    return JSON.stringify(obj);
  } catch (e) {
    return undefined;
  }
};
const decodeDraft = (str) => {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
};

const QuickQuiz = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const role = getQuizRole(user);
  const isP4P = role === QUIZ_ROLE.P4P;

  // Filters live in the URL query string (e.g. ?team=<id>&type=daily&active=true) so the
  // current view is shareable and bookmarkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // For team admins, the channel is themselves. For P4P, picked via TeamPicker and
  // rehydrated from the ?team=<id> param on deep-link/refresh.
  const [team, setTeam] = useState(
    role === QUIZ_ROLE.TEAM
      ? { _id: user._id, name: user.name, username: user.username, avatar: user.avatar }
      : null
  );

  // ?team=all is the cross-channel view. It is deliberately distinct from an absent
  // ?team (nothing picked yet) so the empty state and the "All channels" list don't collide.
  const teamIdParam = searchParams.get('team');
  const allChannels = isP4P && teamIdParam === ALL_CHANNELS;

  useEffect(() => {
    if (!isP4P) return;
    if (!teamIdParam || teamIdParam === ALL_CHANNELS) {
      setTeam(null);
      return;
    }
    if (team?._id === teamIdParam) return;
    let cancelled = false;
    (async () => {
      const res = await Api.post('/admin/getUser', { userId: teamIdParam }, null, {
        silent: true
      });
      if (!cancelled && res?._id) setTeam(res);
    })();
    return () => {
      cancelled = true;
    };
  }, [isP4P, teamIdParam, team?._id]);

  const handleTeamChange = (t) => {
    setTeam(t);
    updateParams({ team: t?._id, page: undefined });
  };

  const handleSelectAllChannels = () => {
    setTeam(null);
    updateParams({ team: ALL_CHANNELS, page: undefined });
  };

  const typeParam = searchParams.get('type') ?? undefined;
  const activeParam = searchParams.get('active') ?? undefined;
  const typeFilter = TYPE_FILTER_OPTIONS.find((o) => o.opt === typeParam) || TYPE_FILTER_OPTIONS[0];
  const activeFilter =
    ACTIVE_FILTER_OPTIONS.find((o) => o.opt === activeParam) || ACTIVE_FILTER_OPTIONS[0];
  // Filter changes reset to page 1 so the list can't land on an out-of-range page.
  const setTypeFilter = (o) => updateParams({ type: o.opt, page: undefined });
  const setActiveFilter = (o) => updateParams({ active: o.opt, page: undefined });

  // Pagination lives in the URL (?page=<n>&limit=<n>) so a paged view is shareable.
  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('limit')) || PAGE;

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [responsesQuiz, setResponsesQuiz] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const addMenuRef = useRef(null);
  useOutsideClick(addMenuRef, () => setAddMenuOpen(false));

  // Create/edit/bulk forms are URL-driven (?mode=create | ?mode=edit&id=<quizId> | ?mode=bulk)
  // so an open form survives refresh and is shareable. The edit target resolves from the list.
  const modeParam = searchParams.get('mode');
  const editIdParam = searchParams.get('id');
  const createMode = modeParam === 'create' || modeParam === 'edit' ? modeParam : null;
  const editingQuiz =
    modeParam === 'edit' ? quizzes.find((q) => q._id === editIdParam) || null : null;
  const bulkOpen = modeParam === 'bulk';
  const initialDraft = useMemo(() => decodeDraft(searchParams.get('draft')), [searchParams]);
  const handleDraftChange = useCallback(
    (draft) => updateParams({ draft: encodeDraft(draft) }),
    [updateParams]
  );

  const teamId = team?._id;
  // Either a channel is picked or "All channels" is active — both are valid scopes to query.
  const scoped = !!teamId || allChannels;

  // Shared by the list and the summary strip so the totals always describe the same rows.
  // teamId is omitted entirely under "All channels"; the backend widens to every channel
  // and gates that on P4P admin.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (teamId) params.set('teamId', teamId);
    // Team admins are forced to DAILY server-side; don't bother sending the filter.
    if (isP4P && typeFilter.opt) params.set('quizType', typeFilter.opt);
    if (activeFilter.opt !== undefined) params.set('isActive', activeFilter.opt);
    return params;
  }, [teamId, isP4P, typeFilter.opt, activeFilter.opt]);

  const fetchQuizzes = useCallback(async () => {
    if (!scoped) return;
    setLoading(true);
    const params = buildFilterParams();
    params.set('limit', String(pageSize));
    params.set('offset', String((page - 1) * pageSize));
    const res = await Api.get(`/admin/quizzes/list?${params.toString()}`);
    if (res?.data) {
      setQuizzes(res.data);
      setTotal(res.pagination?.total || res.data.length);
    }
    setLoading(false);
  }, [scoped, buildFilterParams, page, pageSize]);

  useEffect(() => {
    if (scoped) fetchQuizzes();
  }, [scoped, typeFilter.opt, activeFilter.opt, page, pageSize, fetchQuizzes]);

  // Life-to-date totals for exactly the current filter set. Deliberately independent of
  // page/pageSize — the totals describe the whole filtered set, not the visible page.
  const fetchSummary = useCallback(async () => {
    if (!scoped) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    const res = await Api.get(`/admin/quizzes/summary?${buildFilterParams().toString()}`, null, {
      silent: true
    });
    setSummary(res?.data || null);
    setSummaryLoading(false);
  }, [scoped, buildFilterParams]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleCreate = () => {
    if (!teamId) return;
    setAddMenuOpen(false);
    updateParams({ mode: 'create', id: undefined });
  };

  const handleBulk = () => {
    if (!teamId) return;
    setAddMenuOpen(false);
    updateParams({ mode: 'bulk', id: undefined });
  };

  const closeForm = () => updateParams({ mode: undefined, id: undefined, draft: undefined });

  const toggleAddMenu = () => {
    if (!teamId) return;
    setAddMenuOpen((v) => !v);
  };

  const handleEdit = (quiz) => {
    updateParams({ mode: 'edit', id: quiz._id });
  };

  const handleDelete = (quiz) => {
    setDeleteTarget(quiz);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await Api.delete(`/admin/quizzes/delete/${deleteTarget._id}`);
    setDeleting(false);
    if (ok) {
      setQuizzes((prev) => prev.filter((q) => q._id !== deleteTarget._id));
      setTotal((t) => Math.max(0, t - 1));
      fetchSummary();
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Quiz deleted.' } });
      setDeleteTarget(null);
    }
  };

  const handleSavedSingle = () => {
    fetchQuizzes();
    fetchSummary();
    dispatch({ type: 'HANDLE_RES', payload: { res: 'Quiz saved.' } });
  };

  const handleSavedBulk = (count) => {
    fetchQuizzes();
    fetchSummary();
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: `Created ${count} quiz${count === 1 ? '' : 'zes'}.` }
    });
  };

  const handleToggleActive = (updated) => {
    setQuizzes((prev) => prev.map((q) => (q._id === updated._id ? { ...q, ...updated } : q)));
  };

  if (!role) {
    return (
      <Col centerAll ht="60vh">
        <Text h3>Not authorized</Text>
        <Text col={theme.GREY_60} marg="8px 0 0">
          Quick Quiz management is available to P4P admins and channel owners only.
        </Text>
      </Col>
    );
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>Quick Quiz</Text>

          <div ref={addMenuRef} style={{ position: 'relative' }}>
            <Clickable
              onClick={toggleAddMenu}
              style={{
                opacity: teamId ? 1 : 0.4,
                cursor: teamId ? 'pointer' : 'not-allowed'
              }}
              title={
                teamId
                  ? 'Add a quiz'
                  : allChannels
                    ? 'Pick a single channel first'
                    : 'Pick a channel first'
              }
            >
              <Icon marg="0 0 0 8px" type="add" dimensions={18} />
            </Clickable>

            {addMenuOpen && teamId && (
              <Col
                noFlex
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  minWidth: 240,
                  background: 'white',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  border: `1px solid ${theme.GREY_20}`,
                  padding: 6,
                  zIndex: 20
                }}
              >
                <Clickable
                  onClick={handleCreate}
                  style={{ borderRadius: 6, padding: '10px 12px' }}
                >
                  <Row center>
                    <Icon type="add" dimensions={16} col={theme.PRIMARY} />
                    <Col marg="0 0 0 10px" noFlex>
                      <Text med>Create single quiz</Text>
                      <Text c2 col={theme.GREY_60}>
                        One daily or half-time quiz
                      </Text>
                    </Col>
                  </Row>
                </Clickable>

                <Clickable
                  onClick={handleBulk}
                  style={{ borderRadius: 6, padding: '10px 12px' }}
                >
                  <Row center>
                    <Icon type="calendar" dimensions={16} col={theme.PRIMARY} />
                    <Col marg="0 0 0 10px" noFlex>
                      <Text med>Bulk pre-create</Text>
                      <Text c2 col={theme.GREY_60}>
                        7 / 15 / 30-day daily set
                      </Text>
                    </Col>
                  </Row>
                </Clickable>
              </Col>
            )}
          </div>
        </Row>
      </Header>

      <Col start noFlex marg="54px 0 0" wid="100%" pad="0 24px 24px">
        <Row
          start
          gap="30px"
          noFlex
          wrap
          style={{
            alignItems: 'flex-end',
            position: 'sticky',
            // Sticky relative to the Admin scroll container; sit just below the fixed
            // Header (which is 54px tall — same number used by the wrapper's top margin).
            top: 54,
            background: 'white',
            // Sit below the page Header (z:4) but above the scrolling quiz rows so the
            // sticky filter docks cleanly under the title bar without bleed-through.
            zIndex: 2,
            padding: '12px 24px',
            marginLeft: -24,
            marginRight: -24,
            borderBottom: `1px solid ${theme.GREY_20}`
          }}
        >
          {isP4P && (
            <Col noFlex>
              <Text marg="0 0 4px" b2 med>
                Channel
              </Text>
              <TeamPicker
                team={team}
                onChange={handleTeamChange}
                label={null}
                allChannels={allChannels}
                onSelectAll={handleSelectAllChannels}
              />
            </Col>
          )}

          {isP4P && (
            <Dropdown
              admin
              title="Type"
              wid="180px"
              selected={typeFilter.value}
              options={TYPE_FILTER_OPTIONS}
              handleSelect={setTypeFilter}
              rounded
              small
            />
          )}

          <Dropdown
            admin
            title="Status"
            wid="180px"
            selected={activeFilter.value}
            options={ACTIVE_FILTER_OPTIONS}
            handleSelect={setActiveFilter}
            rounded
            small
          />

          {scoped && (
            <Col noFlex pad="0 0 12px">
              <Text c2 col={theme.GREY_60}>
                {total} quiz{total === 1 ? '' : 'zes'}
              </Text>
            </Col>
          )}
        </Row>

        {!scoped ? (
          <Col centerAll wid="100%" pad="80px 0">
            <Text col={theme.GREY_60} h4>
              {isP4P ? 'Pick a channel to see its quizzes.' : 'Loading your channel...'}
            </Text>
            {isP4P && (
              <Text col={theme.GREY_60} marg="8px 0 0" center>
                Then use the + next to the title to add a single quiz or pre-create a
                7/15/30-day batch.
              </Text>
            )}
          </Col>
        ) : (
          <>
            <ParticipationSummary
              loading={summaryLoading}
              note={
                allChannels
                  ? 'Life-to-date totals across every channel, for the filters above.'
                  : 'Life-to-date totals for this channel, for the filters above.'
              }
              items={[
                { label: 'Quizzes', value: fmtNumber(summary?.quizzes) },
                { label: 'Answers', value: fmtNumber(summary?.answers) },
                {
                  label: 'Correct',
                  value: fmtNumber(summary?.correctAnswers),
                  hint: summary ? fmtAccuracy(summary.accuracy) : undefined
                },
                { label: 'Unique players', value: fmtNumber(summary?.uniquePlayers) },
                { label: 'Points awarded', value: fmtNumber(summary?.pointsAwarded) }
              ]}
            />
            <QuizListTable
              quizzes={quizzes}
              loading={loading}
              isP4P={isP4P}
              showChannel={allChannels}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewResponses={setResponsesQuiz}
              onToggleActive={handleToggleActive}
            />
            {!loading && total > 0 && (
              <Pagination
                page={page}
                totalPages={Math.max(1, Math.ceil(total / pageSize))}
                pageSize={pageSize}
                total={total}
                onPageSizeChange={(size) => updateParams({ limit: size, page: undefined })}
                onPrev={() => updateParams({ page: page - 1 })}
                onNext={() => updateParams({ page: page + 1 })}
                onGoToPage={(p) => updateParams({ page: p })}
              />
            )}
          </>
        )}
      </Col>

      {/* Panels stay mounted so Sidepanel's CSS transition runs on both open and close. */}
      <QuizForm
        // For edit, wait until the target quiz resolves from the list before opening so the
        // form seeds its inputs with the existing values (matters on deep-link / refresh).
        visible={createMode === 'create' || (createMode === 'edit' && !!editingQuiz)}
        user={user}
        teamId={teamId}
        mode={createMode || 'create'}
        quiz={editingQuiz}
        initialDraft={initialDraft}
        onDraftChange={handleDraftChange}
        onClose={closeForm}
        onSaved={handleSavedSingle}
      />

      <BulkCreateForm
        visible={bulkOpen}
        teamId={teamId}
        existingQuizzes={quizzes}
        onClose={closeForm}
        onSaved={handleSavedBulk}
      />

      <QuizParticipantsModal
        visible={!!responsesQuiz}
        quiz={responsesQuiz}
        onClose={() => setResponsesQuiz(null)}
      />

      <IrreversableModal
        visible={!!deleteTarget}
        heading="Delete quiz?"
        topText={deleteTarget ? `"${deleteTarget.question}"` : ''}
        text="This action cannot be undone. Existing user responses will be kept."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        waiting={deleting}
        onConfirm={confirmDelete}
        closeModal={() => !deleting && setDeleteTarget(null)}
        confirmNoClose
      />
    </Col>
  );
};

export default QuickQuiz;
